import { ENV } from '../config/environment'
import { getShop } from './shop'

const SESSION_KEY = 'dt-os-token'

function getAuthHeader(): Record<string, string> {
  const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export class ApiClientError extends Error {
  constructor(
    public readonly status:    number,
    public readonly error:     string,
    public readonly details?:  unknown,
  ) {
    super(error)
    this.name = 'ApiClientError'
  }
}

type RequestOptions = {
  timeout?:    number  // ms — default 10_000
  retries?:    number  // default 1
  retryDelay?: number  // ms — default 500
  signal?:     AbortSignal
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController()
  for (const sig of signals) {
    if (sig.aborted) { controller.abort(); return controller.signal }
    sig.addEventListener('abort', () => controller.abort(), { once: true })
  }
  return controller.signal
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<T> {
  const { timeout = 10_000, retries = 1, retryDelay = 500, signal } = options

  let attempt = 0

  while (true) {
    const controller = new AbortController()
    const timeoutId  = setTimeout(() => controller.abort(), timeout)
    const merged     = signal ? anySignal([signal, controller.signal]) : controller.signal

    try {
      const url = new URL(`${ENV.API_BASE_URL}${path}`)
      const shop = getShop()
      if (shop && !url.searchParams.has('shop')) url.searchParams.set('shop', shop)

      const res = await fetch(url.toString(), {
        ...init,
        headers: { 'Content-Type': 'application/json', ...getAuthHeader(), ...init.headers },
        signal:  merged,
      })

      clearTimeout(timeoutId)

      if (!res.ok) {
        let body: { ok: false; error: string } = { ok: false, error: `HTTP ${res.status}` }
        try { body = await res.json() as typeof body } catch { /* non-JSON error body */ }
        throw new ApiClientError(res.status, body.error, body)
      }

      return res.json() as Promise<T>
    } catch (err) {
      clearTimeout(timeoutId)

      const isAbort     = err instanceof Error && err.name === 'AbortError'
      const isRetryable = !isAbort && attempt < retries

      if (isRetryable) {
        attempt++
        await sleep(retryDelay * attempt)
        continue
      }

      if (isAbort) throw new ApiClientError(408, 'Request timed out or was cancelled')
      throw err
    }
  }
}

export async function getText(path: string, options?: RequestOptions): Promise<string> {
  const { timeout = 10_000, signal } = options ?? {}
  const controller = new AbortController()
  const timeoutId  = setTimeout(() => controller.abort(), timeout)
  const merged     = signal ? anySignal([signal, controller.signal]) : controller.signal

  try {
    const res = await fetch(`${ENV.API_BASE_URL}${path}`, { signal: merged })
    clearTimeout(timeoutId)
    if (!res.ok) throw new ApiClientError(res.status, `HTTP ${res.status}`)
    return res.text()
  } catch (err) {
    clearTimeout(timeoutId)
    if (err instanceof Error && err.name === 'AbortError')
      throw new ApiClientError(408, 'Request timed out')
    throw err
  }
}

export function get<T>(path: string, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(path, { method: 'GET' }, options)
}

export function post<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body) }, options)
}

export function put<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }, options)
}

export function patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, options)
}

export function del<T>(path: string, options?: RequestOptions): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' }, options)
}
